/**
 * AI Visibility Impact Card
 * 
 * Compact sidebar card showing LLM Visibility Score
 * Click to open detail modal
 */

import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { InsightIcon } from '../icons';
import { calculateAIVisibilityImpact } from '../../services/aiVisibilityImpactService';
import { AIVisibilityImpactData } from '../../types/aiVisibilityImpact.types';
import AIVisibilityImpactDetail from './AIVisibilityImpactDetail';

interface AIVisibilityImpactCardProps {
  userId: string;
}

const AIVisibilityImpactCard: React.FC<AIVisibilityImpactCardProps> = ({ userId }) => {
  const [data, setData] = useState<AIVisibilityImpactData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const impactData = await calculateAIVisibilityImpact(userId);
      setData(impactData);
    } catch (error) {
      console.error('Error loading AI Visibility Impact:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    );
  }

  if (!data) return null;

  const { score, trend } = data.score;

  // Get trend icon
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-gray-400';

  // Score color
  const scoreColor = score >= 80 ? 'text-emerald-600' : 
                     score >= 60 ? 'text-blue-600' : 
                     score >= 40 ? 'text-yellow-600' : 'text-gray-600';

  return (
    <>
      <div 
        className="bg-white rounded-lg shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setShowDetail(true)}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <InsightIcon size={20} weight="regular" className="text-[#5F6B3A]" />
              <div>
                <h3 className="font-medium text-sm text-gray-700">Visibility Impact</h3>
                <p className="text-xs text-gray-500">LLM-vindbaarheid</p>
              </div>
            </div>
          </div>

          {/* Score */}
          <div className="mb-3">
            <div className="flex items-baseline space-x-2 mb-1">
              <span className={`text-3xl font-bold ${scoreColor}`}>{score}</span>
              <span className="text-sm text-gray-500">/ 100</span>
              {trend !== 'stable' && (
                <TrendIcon size={16} className={trendColor} />
              )}
            </div>
            <p className="text-xs text-gray-600">
              Hoe goed AI-zoekmachines jouw expertise kunnen begrijpen en aanbevelen
            </p>
          </div>

          {/* Contributing Factors (Status only) */}
          <div className="space-y-2 mb-3">
            {Object.entries(data.factors).slice(0, 3).map(([key, factor]) => (
              <div key={key} className="flex items-center justify-between text-xs">
                <span className="text-gray-600 capitalize">
                  {key === 'mutualRespect' ? 'Mutual Respect' :
                   key === 'expertStatus' ? 'Expert Status' :
                   key === 'networkClubs' ? 'Netwerkclubs' :
                   key === 'collaborations' ? 'Samenwerkingen' : 'Connecties'}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  factor.status === 'groeiend' ? 'bg-green-100 text-green-700' :
                  factor.status === 'stabiel' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {factor.status}
                </span>
              </div>
            ))}
          </div>

          {/* Mini Trend */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Trend (30 dagen)</span>
              <span className={`font-medium ${
                trend === 'up' ? 'text-green-600' :
                trend === 'down' ? 'text-red-600' :
                'text-gray-600'
              }`}>
                {trend === 'up' ? 'Groeiend' : trend === 'down' ? 'Dalend' : 'Stabiel'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <AIVisibilityImpactDetail
          userId={userId}
          data={data}
          onClose={() => setShowDetail(false)}
          onRefresh={loadData}
        />
      )}
    </>
  );
};

export default AIVisibilityImpactCard;
